import path from 'path';
import {
  CDN_DOMAIN,
  MAX_IMAGE_DIMENSION,
  MAX_IMAGE_SIZE,
  MEDIA_ACCEPTS_ARRAY,
  MEDIA_TYPE,
  S3_DOMAIN,
} from '../commons/constants/constants';
import { SceneData } from '../types/SceneData';
import { CommonFileType, ExtendedFile, FileModel, StoreImageFormatTypes } from '../types/CommonTypes';

export const getImageCDNUrl = (url: string): string | undefined => {
  return url ? url.replace(S3_DOMAIN, CDN_DOMAIN) : undefined;
};

export const getSceneThumbnail = (scene: SceneData): string => {
  return scene.sourceFolder
    ? `${scene.sourceFolder.url}/${scene.sourceFolder.originalName}.tiles/thumb.jpg`
    : '';
};

export const isImage = (fileName: string): boolean => {
  if (!fileName) {
    return false;
  }

  const ext = `.${fileName.split('.').pop()}`;
  return MEDIA_ACCEPTS_ARRAY[MEDIA_TYPE.IMAGE].includes(ext.toLowerCase());
};

/**
 *
 * @param imgPath image name include extension
 * @returns Return true when image name have special character, false otherwise
 */
export const validateImageName = (imgPath: string) => {
  const imgExt = path.extname(imgPath);
  const imgName = path.basename(imgPath, imgExt);
  /* eslint-disable no-useless-escape */
  const specialCharacterRegex = /[;,]+/;

  return specialCharacterRegex.test(imgName);
};

export const validatePanoramaImages = async (
  files = []
): Promise<{ isValid: boolean; message: string }> => {
  let isValid = true;
  let message = '';

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    const imgPromise = new Promise<HTMLImageElement>((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;

      img.onload = () => {
        resolve(img);
      };
    });

    /* eslint-disable no-await-in-loop */
    const [img] = await Promise.all([imgPromise]);

    if (validateImageName(file.path)) {
      isValid = false;
      message = 'Filename must have no special character';
      return { isValid, message };
    }

    if (img.width < img.height * 2) {
      isValid = false;
      message = 'Kích thước ảnh không đúng tỉ lệ 2:1';
      return { isValid, message };
    }
    // check image file dimension
    const isValidImageDimension =
      img.width <= MAX_IMAGE_DIMENSION && img.height <= MAX_IMAGE_DIMENSION;
    // check image file size
    if (!isValidImageDimension) {
      isValid = false;
      message = 'Kích thước ảnh quá lớn không hợp lệ';
      return { isValid, message };
    }
    // max size image = 50Mb
    const isValidFileSize = file.size <= MAX_IMAGE_SIZE;
    if (!isValidFileSize) {
      isValid = false;
      message = 'Kích thước file ảnh không hợp lệ';
      return { isValid, message };
    }

    // create element canvas
    let canvas: HTMLCanvasElement = document.createElement('canvas');
    // set dimension canvas equal image input
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    // set format 2d canvas
    const ctx: CanvasRenderingContext2D = canvas.getContext('2d');
    // method of the Canvas 2D API provides different ways to draw an image onto the canvas. vẽ ảnh lên canvas
    ctx.drawImage(img, 0, 0);

    const dataURL = canvas.toDataURL('image/png');
    const base64 = dataURL.split('base64,')[1];
    // reset canvas
    if (canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
    } else {
      canvas = null;
    }

    // convert canvas base 64 to bytes array
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let j = 0; j < byteCharacters.length; j++) {
      byteNumbers[j] = byteCharacters.charCodeAt(j); // representing the UTF-16 code
    }

    const bytes2Int = (dataBytes: Uint8Array) => {
      let ret = 0;
      for (let idx = 0; idx < dataBytes.length; idx++) {
        ret += dataBytes[idx];

        if (idx < dataBytes.length - 1) {
          ret <<= 8;
        }
      }

      return ret;
    };

    const parseChunk = (dataBytes: Uint8Array, start: number) => {
      const chunkLength = bytes2Int(dataBytes.slice(start, start + 4));

      let chunkName = '';
      chunkName += String.fromCharCode(dataBytes[start + 4]);
      chunkName += String.fromCharCode(dataBytes[start + 5]);
      chunkName += String.fromCharCode(dataBytes[start + 6]);
      chunkName += String.fromCharCode(dataBytes[start + 7]);

      const chunkData = [];
      for (let idx = start + 8; idx < chunkLength + start + 8; idx++) {
        chunkData.push(dataBytes[idx]);
      }
      // TODO validate crc as required!

      return {
        start: start,
        end: Number(start) + Number(chunkLength) + 12, // 12 = 4 (length) + 4 (name) + 4 (crc)
        length: chunkLength,
        name: chunkName,
        data: new Uint8Array(chunkData),
        crc: [
          dataBytes[chunkLength + start + 8],
          dataBytes[chunkLength + start + 9],
          dataBytes[chunkLength + start + 10],
          dataBytes[chunkLength + start + 11],
        ],
        crcChecked: false,
      };
    };

    const bytes = new Uint8Array(byteNumbers);

    // check color mode
    const chunk = parseChunk(bytes, 8);

    if (chunk.name === 'IHDR') {
      const colorType = bytes2Int(chunk.data.slice(9, 10));
      const isRgb = colorType === 2 || colorType === 6;
      const bitDepth = bytes2Int(chunk.data.slice(8, 9));
      if (!isRgb || !(bitDepth === 8 || bitDepth === 16)) {
        isValid = false;
        message = 'Color mode ảnh không hợp lệ';
        return { isValid, message };
      }
    }
  }

  return { isValid, message };
};


export const getImageSrc = (image: CommonFileType): string => {
  const imgFile = (image as FileModel);
  const imgExtFile = (image as ExtendedFile);
  if (imgFile.url) {
    return imgFile.url;
  }
  if (imgExtFile.preview) {
    return imgExtFile.preview;
  }
};


export const getImageObject = (img: FileModel, boxWidth?: number): StoreImageFormatTypes => {
  if (!img || !img.responsive) return img;
  const { large, medium, small, thumbnail } = img.responsive;

  let scaleRatio = 1;
  if (img.width && img.height && !boxWidth) {
    scaleRatio = img.width > img.height ? img.height / img.width : img.width / img.height;
  }

  let currentWidth = 0;
  if (boxWidth) {
    currentWidth = boxWidth;
  } else if (!boxWidth && typeof window === 'object') {
    currentWidth = window.innerWidth;
  }

  if (scaleRatio !== 1) currentWidth /= scaleRatio;
  const largeSize = 1000;
  const mediumSize = 750;
  const smallSize = 500;
  const thumbSize = 250;

  if (currentWidth === 0) return null;
  if (currentWidth > largeSize) return img;

  if (
    Math.max(largeSize, currentWidth) === largeSize &&
    Math.min(mediumSize, currentWidth) === mediumSize
  ) {
    return large || img;
  }

  if (
    Math.max(mediumSize, currentWidth) === mediumSize &&
    Math.min(smallSize, currentWidth) === smallSize
  ) {
    return medium || img;
  }

  if (
    Math.max(smallSize, currentWidth) === smallSize &&
    Math.min(thumbSize, currentWidth) === thumbSize
  ) {
    return small || img;
  }

  if (currentWidth < thumbSize) {
    return thumbnail || img;
  }
};

export const getImageUrl = (img: FileModel, boxWidth?: number): string => {
  const imageObj = getImageObject(img, boxWidth);
  if (!imageObj) return null;

  return imageObj.url;
};

